/**
 * Legacy KV ("Kundenverwaltung") billing feature texts and order (EB-4502).
 *
 * COPIED, not composed: generated from the hardcoded arrays in
 *   pinguin_repo origin/master 55e211d443,
 *   ebuero-ui/src/main/java/com/ebuero/billing/admin/KundenBillingFrame.java:157-248
 *   (`nameGruppen`, `nameFeatureShow[][]` <-> `nameFeature[][]`).
 *
 * Keyed by LegacyCategory key (billing-categories.ts), because a code can carry a
 * different KV text per group (`brief_scan`: Postservice vs Office Club). Array order
 * is the KV dropdown order. `Nutzungsguthaben` is omitted: KV books it into the
 * separate Guthaben entity, AMIE handles it as Usage Credit (EB-3883).
 *
 * The third element is a CORRECTED display text where the KV text is wrong (typo,
 * outdated VAT rate, Italian text). The original stays available for the tooltip.
 */

/** `[code, kvText, correctedText?]` — KV text exactly as in KundenBillingFrame.java. */
export type KvFeatureEntry = readonly [code: string, kvText: string, corrected?: string];

export const KV_FEATURE_LABELS: Readonly<Record<string, readonly KvFeatureEntry[]>> = {
  GUTSCHRIFTEN: [
    ['gut_gut', 'Gutschrift, allgemein'],
    ['gut_grund', 'Gutschrift, Grundgebühr'],
    ['gut_subaccount', 'Gutschrift Zusatzsekretariat'],
    ['gut_calls', 'Gutschrift Anrufe'],
    ['gut_FFU', 'Gutschrift Friendly Follow Up'],
    ['gut_24/7', 'Gutschrift 24/7'],
    ['gut_number', 'Gutschrift zusätzliche Rufnummer'],
    ['gut_credit', 'Gutschrift, Credits'],
  ],
  SEKRETARIATSDIENSTE: [
    ['callsekret', 'Sekretariatsdienst Anrufe'],
    ['callsekret_1min', 'Sekretariatsdienst Anrufe für 1 minute', 'Sekretariatsdienst Anrufe für 1 Minute'],
    ['ab_einrichtung', 'Aufnahme eines Ansagetexts'],
  ],
  POSTSERVICE: [
    ['brief_empf', 'Briefannahme / -weiterleitung pro Brief'],
    ['paket_empf', 'Paketannahme / -weiterleitung (< 5kg) pro Paket'],
    ['paket_empf_zuschlag', 'Zuschlag Paketannhme / -weiterleitung (>5kg) pro kg', 'Zuschlag Paketannahme / -weiterleitung (>5kg) pro kg'],
    ['porto', 'Porto (Brief/Paket)'],
    ['paket_lagerung', 'Paketlagerung pro Paket und Tag (< 5kg)'],
    ['paket_lagerung_zuschlag', 'Zuschlag Paketlagerung (>5kg) pro kg und Tag'],
    ['brief_vers', 'Briefversand über Webinterface'],
    ['brief_scan', 'Scan Service'],
    ['brief_query', 'Telefonische Abfrage Eingangspost'],
  ],
  RAUMSERVICE: [
    ['room_DUS', 'Raumbuchung Domizil (Dussmann)'],
    ['room_light_DUS', 'Raumbuchung OHNE Domizil (Dussmann)'],
    ['storno', 'Stornogebühr für Raumbuchung'],
    ['storno_day_before_DUS', 'Stornogebühr für Raumbuchung (Dussmann, Tag vorher)'],
    ['storno_same_day_DUS', 'Stornogebühr für Raumbuchung (Dussmann, gleicher Tag)'],
    ['room', 'Raumbuchung Domizil'],
    ['room_light', 'Raumbuchung OHNE Domizil'],
    ['office_service', 'Allgemeine Bürotätigkeiten'],
    ['print_color', 'Ausdruck Farbe A4'],
    ['beamer', 'Beamer'],
    ['beamer_1day', 'Beamer ganzer Tag'],
    ['beamer_4h', 'Beamer halber Tag'],
    ['beamer_1h', 'Beamer je Stunde'],
    ['laptop_1day', 'Laptop ganzer Tag'],
    ['laptop_4h', 'Laptop halber Tag'],
    ['laptop_1h', 'Laptop je Stunde'],
    ['monitor_full_hd', 'Monitor Full-HD'],
    ['monitor_full_hd_1day', 'Monitor Full-HD ganzer Tag'],
    ['monitor_full_hd_4h', 'Monitor Full-HD halber Tag'],
    ['monitor_full_hd_1h', 'Monitor Full-HD je Stunde'],
    ['konferenztelefon_1day', 'Konferenztelefon ganzer Tag'],
    ['konferenztelefon_4h', 'Konferenztelefon halber Tag'],
    ['konferenztelefon_1h', 'Konferenztelefon je Stunde'],
    ['moderationskoffer', 'Moderationskoffer'],
    ['botengang', 'Botengang extern'],
    ['office_material', 'Büromaterial'],
    ['fax_office', 'Faxein-/ausgang vor Ort'],
    ['drinks_juice', 'Getränke - Säfte'],
    ['coffee', 'Getränke - Tee/ Kaffee'],
    ['coffee_free', 'Getränke - Tee/ Kaffee (kostenlos)'],
    ['drinks_water', 'Getränke - Wasser'],
    ['drinks', 'Getränke'],
    ['snacks', 'Snacks'],
    ['copy_b/w', 'Kopie s/w A4'],
    ['copy_service', 'Kopierservice Bedienung'],
    ['print_b/w', 'Laserdruck s/w A4'],
    ['flex_office_1day', 'Miete Flexbüro ganzer Tag'],
    ['flex_office_4h', 'Miete Flexbüro halber Tag'],
    ['flex_office_1h', 'Miete Flexbüro je Stunde'],
    ['catering_service', 'Service Catering'],
    ['other_service', 'Sonstige Dienstleistungen'],
    ['catering', 'Speisen/ Catering'],
    ['storno_day_before', 'Stornogebühr für Raumbuchung (Tag vorher)'],
    ['storno_same_day', 'Stornogebühr für Raumbuchung (gleicher Tag)'],
    ['phone_germany', 'Telefongebühren vor Ort (Deutschland)'],
    ['text_german', 'Textverarbeitung (Deutsch)'],
    ['text_foreign', 'Textverarbeitung (fremdsprachig)'],
    ['wlan', 'WLAN-Nutzung (pro Tag)'],
    // Corrected text = invoice text: localization-api live, featureShowname/defTarif,
    // brand 1, language 1, key additionalChargeOvertime (checked 2026-10-19).
    ['additionalChargeOvertime', 'Sovrapprezzo per affitto fuori orario', 'Gebühr für längere Nutzung des Meetingraums'],
  ],
  NKOHL: [
    // 19%: the invoice text says "(19% USt)" — localization-api live, featureShowname/defTarif,
    // brand 1, language 1, key nkohl_gk_satz1 (checked 2026-10-19).
    ['nkohl_gk_satz1', 'Gerichtskosten MWSTSatz 1 (16%)', 'Gerichtskosten MWSTSatz 1 (19%)'],
    ['nkohl_gk_satz0', 'Gerichtskosten MWSTSatz 0 (0%)'],
  ],
  DIKTAT: [
    ['diktat_overnight', 'Tarif Overnight (in Min)'],
    ['diktat_3days', 'Tarif 3 Wektage (in Min)', 'Tarif 3 Werktage (in Min)'],
    ['diktat_5days', 'Tarif 5 Werktage (in Min)'],
  ],
  WARTEMUSIK: [
    ['mohCreateStandard', 'Wartemusik Standard'],
    ['mohCreatePremium', 'Wartemusik Premium'],
  ],
  LOGO: [
    ['logo_1proposal', 'Logo 1 Vorschlag'],
    ['logo_3proposal', 'Logo 3 Vorschläge'],
    ['logo_5proposal', 'Logo 5 Vorschläge'],
    ['logo_claim', 'Logo Erweiterung: Claim'],
    ['logo_extra', 'Extra Logo'],
  ],
  SONDERDIENSTLEISTUNGEN: [
    ['evn', 'Erstellung EVN'],
    ['form_erst', 'Formularerstellung'],
  ],
  SONDERAKTIONEN: [
    ['aktion_tel', 'Sonderaktion Telefonsekretariat'],
    ['aktion_ga', 'Sonderaktion Geschäftsadresse'],
  ],
  EINRICHTUNGSGEBUEHREN: [
    ['setup_number', 'Einrichtung lokale Nummer'],
    ['setup_firmenschild', 'Einrichtung Firmenschild'],
  ],
  POSTRUECKLAEUFER: [
    ['adress_erm5', 'Adressermittlung 5 Euro'],
    ['adress_erm10', 'Adressermittlung 10 Euro'],
    ['adress_erm22', 'Adressermittlung 22 Euro'],
  ],
  ZUSAETZLICHE_GEBUEHREN: [
    ['fees_subsequent', 'Gebühren Nachberechnung'],
    ['fee_CompanyRegistry', 'Gebühr Handelsregisterauszug'],
    ['fees_additional', 'zusätzliche Gebühren'],
    ['packageUpgrade', 'Paket-Upgrade'],
  ],
  PROVISION: [
    ['provisionKvm', 'Provision Kundenvermittlung'],
  ],
  MULTIPHONESERVICE: [
    ['training_fee', 'Schulungspauschale (1 Stunde)'],
    ['object_mapping', 'Objektzuordung', 'Objektzuordnung'],
    ['backoffice', 'Backoffice-Projekt (1 Stunde)'],
  ],
  OFFICE_CLUB: [
    ['sekret', 'Sekretariatsdienst (5 Min.)'],
    ['brief_scan', 'Scannen eines Briefes'],
    ['tea', 'Tea'],
    ['bitebox', 'BiteBox Snack'],
    ['charitea', 'ChariTea'],
    ['annahme_von_einschreiben', 'Annahme von Einschreiben'],
    ['locker', 'Schließfach'],
    ['telefon', 'Telefon (Endgerät)'],
    ['firmenschild', 'Firmenschild'],
    ['paket_handling', 'Handling eines Briefversands'],
    ['catering', 'Speisen/ Catering'],
    ['kurier', 'Kurierdienst (€)'],
    ['post_vers', 'Frankieren/Versand (Stck)'],
    ['room_1h', 'Konferenzraumnutzung (1h)'],
    ['scan', 'Scannen von Dokumenten (Stck)'],
    ['fritz_kola', 'Fritz-Kola div.'],
    ['club_mate', 'Club Mate'],
    ['saft_bio', 'Saft Bio'],
    ['bier_div', 'Bier'],
    ['sekt', 'Sekt'],
    ['illy_kaffee', 'Kaffee / Americano'],
    ['illy_michkaffee', 'Milchkaffee'],
    ['illy_cappuccino', 'Cappuccino'],
    ['illy_latte_macchiato', 'Latte Macchiato'],
    ['illy_espresso_doppio', 'Espresso doppio'],
    ['illy_espresso', 'Espresso'],
    ['trinkschokolade_div', 'Heiße Schokolade'],
    ['extra_day', 'Zusatztag'],
    ['copy_A3_b/w', 'Kopie s/w A3'],
    ['green_card', 'Verl. / Neuausst. Club Card'],
    ['postw_taglich', 'Postweiterleitung'],
    ['print_color_A3', 'Ausdruck Farbe A3'],
    ['duplo', 'Duplo'],
    ['kinderriegel', 'Kinderriegel'],
    ['balisto', 'Balisto'],
    ['kuchen_div', 'Kuchen div.'],
    ['getranke', 'Heißgetränke-Flatrate'],
    ['whiteboard', 'Whiteboard / Flipchart'],
    ['funkmikrofone', 'Funk-Mikrofon'],
    ['rednerpult', 'Rednerpult'],
    ['gebackteller', 'Gebäckteller'],
    ['coffee_1l', 'Kaffee je Kanne - 1L'],
    ['tea_1l', 'Tee je Kanne - 1L'],
    ['lemonaid', 'Lemonaid div.'],
    ['twixbistro', 'Twix'],
    ['trial_day', 'Tagespass'],
    ['monitor_hosting', 'Monitor Hosting'],
    ['cowo_club_247_access', 'Office Club 24/7 access'],
    ['internet_lan', 'Internet via LAN'],
    ['briefumschlag', 'Briefumschlag'],
    ['einrichtungsgebuehr', 'Einrichtungsgebühr'],
    ['wasa_einzeln', 'Wasa einzeln'],
    ['sms_versand', 'SMS Versand'],
    ['letter_black_white_page', 'Briefseite (schwarz-weiß)'],
    ['letter_colored_page', 'Briefseite (bunt)'],
    ['letter_envelope_c5', 'Umschlag (DIN LANG)'],
    ['letter_envelope_c4', 'Umschlag (C4-Kuvert)'],
    ['letter_registered_insertion', 'Einschreiben (Einwurf)'],
    ['local_phonenumber', 'Lokale Ortsrufnummer'],
    ['0800_phonenumber', '0800-Rufnummer'],
    ['0180-5_phonenumber', '0180-5-Rufnummer'],
    ['forwarding_fixed_line', 'Weiterleitung dt. Festnetz / Min.'],
    ['forwarding_europe_and_usa', 'Weiterleitung Europa & USA / Min.'],
    ['forwarding_mobile', 'Weiterleitung mobil / Min.'],
    ['forwarding_world', 'Weiterleitung restliche Welt / Min.'],
    ['accepted_call_pickup', 'Gesprächsannahme Sonderrufnummer Festnetz'],
    ['accepted_call_min', 'Gesprächsminute Sonderrufnummer Festnetz'],
    ['postal_invoice', 'Rechnung per Post'],
    ['event', 'Event'],
    ['knoppers', 'Knoppers'],
    ['kitkat', 'KitKat'],
    ['ausgleichfehlbestand', 'Ausgleich Fehlbestand Kasse'],
    ['getraenke_basic', 'Getränkepauschale p.P.'],
    ['meeting_manuell', 'Meeting'],
    ['personal', 'Personal'],
    ['barzahlung_rng', 'Barzahlung Rechnung'],
    ['parkplatz', 'Parkplatz'],
    ['handling_brief', 'Handling Brief'],
    ['flex_member', 'Flex-Mitgliedschaft'],
    ['freedrink_tariff_membership', 'Freigetränk Membership-Tarif'],
    ['freemonth_user_vacation', 'Freimonat Urlaubsschaltung (Membership-Tarif)'],
    ['desk', 'Schreibtisch'],
    ['infomail_absender', 'Infomail-Service'],
    ['flat_white', 'Flat White'],
    ['fruits', 'Obstkorb'],
    ['mmsklein', 'M&Ms'],
    ['maske', 'Mund-Nasen-Schutz'],
    ['zusatz_empfaenger', 'Zusätzliche Marke / Empfänger'],
    ['aufwand', 'Aufwandsentschädigung'],
    ['nespresso', 'Nespresso'],
    ['huoberbrezeln', 'Huober Minis - Brezeln'],
    ['specialdrink', 'Saison-Special Getränk'],
    ['pausensnack', 'Pausensnack p.P.'],
    ['fruehstueckspauschale', 'Frühstückspauschale p.P.'],
    ['verfplegungspauschale', 'Verpflegungspauschale p.P.'],
    ['cremant', 'Crémant'],
    ['moebel', 'Mietmöbel'],
    ['telephone_forwarding', 'Telefonweiterleitung / Annahme'],
    ['zusaetzlicher_arbeitsplatz', 'Zusätzlicher Arbeitsplatz'],
    ['postal_handling', 'Wöchentliche Postsammelzustellung'],
    ['postal_collection', 'Postselbstabholung (Lagergebühr)'],
    ['video_konferenz', 'Videokonferenzsystem'],
    ['moebel_verkauf', 'Verkauf Möbel / Inventar'],
    ['telefonbuch_eintrag', 'Telefonbucheintrag'],
    ['cleaning', 'Endreinigung'],
  ],
};
